import { useState } from 'react';
import { InboxIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput, SkeletonTable, StatTile, type PillTone } from '@bcecloud/portal-ui';
import { getJson, errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { salesforceLeadUrl } from '@/lib/leads';
import { useTableSort } from '@/lib/use-table-sort';

/** How a referral went. The API's values (partner-referrals.ts). */
const REFERRAL_STATUS = {
  received: 'RECEIVED',
  inDiscussion: 'IN_DISCUSSION',
  booked: 'BOOKED',
  notBooked: 'NOT_BOOKED',
} as const;
type ReferralStatus = (typeof REFERRAL_STATUS)[keyof typeof REFERRAL_STATUS];

const STATUS_DISPLAY: Readonly<Record<ReferralStatus, { readonly label: string; readonly tone: PillTone }>> = {
  [REFERRAL_STATUS.received]: { label: 'Received', tone: 'info' },
  [REFERRAL_STATUS.inDiscussion]: { label: 'In discussion', tone: 'warning' },
  [REFERRAL_STATUS.booked]: { label: 'Booked', tone: 'success' },
  [REFERRAL_STATUS.notBooked]: { label: "Didn't book", tone: 'neutral' },
};

/** One referral as staff see it (GET /organizations/:id/partner-referrals). */
interface StaffReferral {
  readonly id: string;
  readonly receivedAt: string;
  readonly activity: string | null;
  readonly groupSize: string | null;
  readonly eventMonth: string | null;
  readonly status: ReferralStatus;
  readonly name: string;
  readonly company: string | null;
  readonly email: string;
  readonly salesforceLeadId: string | null;
  readonly stageName: string | null;
  readonly bookedValue: number | null;
}

interface ReferralsResponse {
  readonly referrals: readonly StaffReferral[];
  readonly counts: { readonly total: number; readonly received: number; readonly inDiscussion: number; readonly booked: number; readonly notBooked: number };
}

const SORT_FIELDS = {
  received: 'receivedAt',
  person: 'personLabel',
  activity: 'activityLabel',
  status: 'statusLabel',
  value: 'valueSort',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);

const usd = (value: number): string => value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const shortDate = (iso: string): string => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Every lead a partner's showcase sent, for staff: who asked, what came of
 * it in Salesforce, and what each booking was worth. The partner sees the
 * same rows with the status only.
 */
export function OrgPartnerReferralsPanel({ organizationId, organizationName }: { readonly organizationId: string; readonly organizationName: string }): React.ReactElement {
  const { sort, onSort } = useTableSort(SORT_KEYS, { key: SORT_FIELDS.received, direction: 'desc' });
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  const referrals = useQuery({
    queryKey: ['partner-referrals', organizationId],
    queryFn: ({ signal }) => getJson<ReferralsResponse>(`/organizations/${organizationId}/partner-referrals`, undefined, signal),
  });

  const all = referrals.data?.referrals ?? [];
  const bookedValue = all.reduce((sum, r) => sum + (r.bookedValue ?? 0), 0);
  const rows = sortRows(
    all
      .map((r) => ({
        ...r,
        personLabel: r.company ? `${r.name} ${r.company}` : r.name,
        activityLabel: r.activity ?? '',
        statusLabel: STATUS_DISPLAY[r.status].label,
        valueSort: r.bookedValue ?? -1,
      }))
      .filter((r) => needle.length === 0 || [r.name, r.company, r.email, r.activity, r.statusLabel, r.stageName].some((f) => (f ?? '').toLowerCase().includes(needle))),
    sort,
  );
  const counts = referrals.data?.counts;

  return (
    <div className="mt-6 space-y-4">
      <div>
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">Referrals</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          Quote requests from {organizationName}'s showcase, matched to their Salesforce Lead. {organizationName} sees each one's status, never the person or the value.
        </p>
      </div>

      {counts ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile label="Leads sent" value={counts.total.toLocaleString('en-US')} hint={`${(counts.received + counts.inDiscussion).toLocaleString('en-US')} still open`} />
          <StatTile label="Booked" value={counts.booked.toLocaleString('en-US')} hint={counts.total === 0 ? 'None yet' : `${Math.round((counts.booked / counts.total) * 100)}% of leads`} />
          <StatTile label="Booked value" value={usd(bookedValue)} hint="Signed contracts" />
          <StatTile label="Didn't book" value={counts.notBooked.toLocaleString('en-US')} hint="Lost, canceled or gone quiet" />
        </div>
      ) : null}

      <div className="flex justify-end">
        <SearchInput
          id="partner-referrals-search"
          label="Search referrals"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search referrals…"
        />
      </div>

      {referrals.isPending ? (
        <SkeletonTable rows={4} columns={['w-[12%]', 'w-[28%]', 'w-[22%]', 'w-[18%]', 'w-[12%]']} label="Loading referrals…" />
      ) : referrals.isError ? (
        <ErrorNotice title="Referrals unavailable" message={errorMessage(referrals.error)} />
      ) : rows.length === 0 && needle.length > 0 ? (
        <Panel>
          <EmptyState icon={<InboxIcon aria-hidden className="size-6" />} title="No matches" message={`No referral matches “${search.trim()}”.`} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<InboxIcon aria-hidden className="size-6" />}
            title="No referrals yet"
            message="When a group requests a quote through this partner's showcase, it appears here with its Lead and outcome."
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Received', sortKey: SORT_FIELDS.received },
            { label: 'Person', sortKey: SORT_FIELDS.person },
            { label: 'Activity', sortKey: SORT_FIELDS.activity },
            { label: 'Status', sortKey: SORT_FIELDS.status },
            { label: 'Booked value', sortKey: SORT_FIELDS.value },
          ]}
          rightAlignedHeaders={[4]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{shortDate(row.receivedAt)}</td>
              <td className="p-3 text-portal-small">
                {row.salesforceLeadId ? (
                  <a
                    href={salesforceLeadUrl(row.salesforceLeadId)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-primary underline-offset-2 hover:underline"
                    aria-label={`Open ${row.name} in Salesforce`}
                  >
                    {row.name}
                  </a>
                ) : (
                  <span className="font-bold">{row.name}</span>
                )}
                <span className="block text-portal-micro text-portal-muted">{[row.company, row.email].filter(Boolean).join(' · ')}</span>
              </td>
              <td className="p-3 text-portal-small">
                {row.activity ?? <span className="text-portal-muted">Not chosen</span>}
                {row.groupSize ? <span className="block text-portal-micro text-portal-muted">{row.groupSize} people</span> : null}
              </td>
              <td className="p-3 text-portal-small">
                <Pill tone={STATUS_DISPLAY[row.status].tone}>{row.statusLabel}</Pill>
                <span className="block text-portal-micro text-portal-muted">{row.stageName ?? (row.salesforceLeadId ? 'Lead, no opportunity yet' : 'Not in Salesforce yet')}</span>
              </td>
              <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                {row.bookedValue === null ? <span className="text-portal-muted">Not booked</span> : usd(row.bookedValue)}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
